import React, { useState, useEffect } from 'react';
import { Zap, Sparkles, Check, LayoutTemplate, X } from 'lucide-react';
import { Button, TextField, Toggle, Autocomplete } from '@anyemp/ui-kit';
import toast from 'react-hot-toast';
import { talentTemplates, libraries, statuses, promptTemplates } from '../../services/apiService';
import ModalPortal from '../UI/ModalPortal';
import ModalFrame from '../UI/ModalFrame';

const DEFAULT_BLOCKS = {
  responsibilities: true,
  cv: true,
  media: false,
  languages: true,
  tools: true,
  professions: true
};

const BLOCK_META = {
  professions:      { label: 'Professions',      icon: '🎯' },
  responsibilities: { label: 'Responsibilities', icon: '📋' },
  tools:            { label: 'Tools & Skills',    icon: '🛠' },
  languages:        { label: 'Languages',         icon: '🌐' },
  cv:               { label: 'CV / Experience',   icon: '📁' },
  media:            { label: 'Media',             icon: '🖼' },
};

const buildName = (toolName, clientName) => {
  const parts = [toolName, clientName].filter(Boolean);
  return parts.join(' – ');
};

/* ─── Block toggle chip ─────────────────────────────────────────────── */
const BlockChip = ({ label, icon, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`
      relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
      border transition-all duration-150 select-none
      ${active
        ? 'bg-emerald-50 dark:bg-emerald-900/25 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 shadow-sm'
        : 'bg-light-bg-primary dark:bg-dark-bg-primary border-light-border-primary dark:border-dark-border-primary text-light-text-tertiary dark:text-dark-text-tertiary hover:border-light-border-secondary dark:hover:border-dark-border-secondary hover:text-light-text-secondary dark:hover:text-dark-text-secondary'
      }
    `}
  >
    <span>{icon}</span>
    <span>{label}</span>
    {active && (
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 ml-0.5 shrink-0" />
    )}
  </button>
);

/* ─── Divider with label ────────────────────────────────────────────── */
const SectionDivider = ({ icon: Icon, label, accent }) => (
  <div className="flex items-center gap-2">
    <div className="flex-1 h-px bg-light-border-primary dark:bg-dark-border-primary" />
    <div className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${accent || 'text-light-text-tertiary dark:text-dark-text-tertiary'}`}>
      {Icon && <Icon size={10} />}
      <span>{label}</span>
    </div>
    <div className="flex-1 h-px bg-light-border-primary dark:bg-dark-border-primary" />
  </div>
);

const LABEL = 'block text-[10px] font-bold uppercase tracking-widest text-light-text-tertiary dark:text-dark-text-tertiary mb-1.5';

// Autocomplete requires string option values/selection; ids coming straight
// from the API are numbers — normalize at the form-state boundary.
const strOrNull = (v) => (v != null ? String(v) : null);

const TalentTemplateModal = ({ isOpen, onClose, template, onSaved }) => {
  const isEdit = Boolean(template?.id);

  const [form, setForm] = useState({
    name: '', tool_id: null,
    inner_client_id: null, inner_client_iso: '',
    status_id: null, prompt_id: null,
    blocks_config: { ...DEFAULT_BLOCKS },
    is_draft: true
  });
  const [saving, setSaving] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [toolOptions, setToolOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [innerClientOptions, setInnerClientOptions] = useState([]);
  const [innerClientRaw, setInnerClientRaw] = useState([]);
  const [promptOptions, setPromptOptions] = useState([]);
  const [promptsRaw, setPromptsRaw] = useState([]);
  const [promptMatch, setPromptMatch] = useState({ exact: null, suggested: [] });

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit && template) {
      setForm({
        name: template.name || '',
        tool_id: strOrNull(template.tool_id),
        inner_client_id: strOrNull(template.inner_client_id),
        inner_client_iso: template.inner_client_iso || '',
        status_id: strOrNull(template.status_id),
        prompt_id: strOrNull(template.prompt_id),
        blocks_config: { ...DEFAULT_BLOCKS, ...(template.blocks_config || {}) },
        is_draft: Boolean(template.is_draft)
      });
    } else {
      setForm({ name: '', tool_id: null, inner_client_id: null, inner_client_iso: '', status_id: null, prompt_id: null, blocks_config: { ...DEFAULT_BLOCKS }, is_draft: true });
    }
    loadOptions();
  }, [isOpen, template]);

  const loadOptions = async () => {
    setOptionsLoading(true);
    try {
      const [toolsRes, statusesRes, innerClientsRes, promptsRes] = await Promise.all([
        libraries.getContactTools().catch(() => ({ data: [] })),
        statuses.getStatusesByType('talent_template', { isCommon: 1 }).catch(() => ({ data: [] })),
        libraries.getInnerClients().catch(() => ({ data: [] })),
        promptTemplates.getAll({ all: true, isCommon: 1, is_draft: false }).catch(() => ({ data: [] }))
      ]);
      const tools = Array.isArray(toolsRes?.data) ? toolsRes.data : (Array.isArray(toolsRes) ? toolsRes : []);
      setToolOptions(tools.map(t => ({ value: String(t.id), label: t.name || t.title })));
      const statusList = Array.isArray(statusesRes?.data) ? statusesRes.data : (Array.isArray(statusesRes) ? statusesRes : []);
      setStatusOptions(statusList.map(s => ({ value: String(s.id), label: s.name })));
      if (!template?.id) {
        const activeStatus = statusList.find(
          (s) => String(s.name || '').toLowerCase() === 'active'
        );
        if (activeStatus) {
          setForm((prev) =>
            prev.status_id == null ? { ...prev, status_id: String(activeStatus.id) } : prev
          );
        }
      }
      const innerArr = Array.isArray(innerClientsRes?.data) ? innerClientsRes.data : [];
      setInnerClientRaw(innerArr);
      setInnerClientOptions(innerArr.map(c => ({ value: String(c.id), label: c.name })));
      const prompts = Array.isArray(promptsRes?.data) ? promptsRes.data : [];
      setPromptsRaw(prompts);
      setPromptOptions(prompts.map(p => ({ value: String(p.id), label: p.name })));
    } catch (err) {
      console.error('Failed to load options:', err);
    } finally {
      setOptionsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const fetchMatch = async () => {
      const params = {};
      if (form.tool_id) params.tool_id = form.tool_id;
      if (form.inner_client_id) params.inner_client_id = form.inner_client_id;
      try {
        const res = await promptTemplates.match(params).catch(() => ({ data: { exact: null, suggested: [] } }));
        const { exact, suggested } = res?.data || { exact: null, suggested: [] };
        setPromptMatch({ exact: exact || null, suggested: suggested || [] });
        // auto-select exact only for new templates (no existing prompt_id)
        if (exact) {
          setForm(prev => prev.prompt_id ? prev : { ...prev, prompt_id: String(exact.id) });
        }
      } catch (err) {
        console.error('Failed to match prompts:', err);
      }
    };
    fetchMatch();
  }, [form.tool_id, form.inner_client_id, isOpen]);

  const handleBlockToggle = (key) => {
    setForm(prev => ({ ...prev, blocks_config: { ...prev.blocks_config, [key]: !prev.blocks_config[key] } }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.status_id)   { toast.error('Status is required'); return; }
    if (!form.prompt_id)   { toast.error('Prompt is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        tool_id: form.tool_id ? parseInt(form.tool_id) : null,
        inner_client_id: form.inner_client_id ? parseInt(form.inner_client_id) : null,
        inner_client_iso: form.inner_client_iso || null,
        status_id: form.status_id ? parseInt(form.status_id) : null,
        prompt_id: form.prompt_id ? parseInt(form.prompt_id) : null,
        blocks_config: form.blocks_config,
        is_draft: form.is_draft
      };
      if (isEdit) {
        await talentTemplates.update(template.id, payload);
        toast.success('Template updated');
      } else {
        await talentTemplates.create(payload);
        toast.success('Template created');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const activeBlocks = Object.values(form.blocks_config).filter(Boolean).length;

  if (!isOpen) return null;

  return (
    <ModalPortal onClose={saving ? undefined : onClose} ariaLabel={isEdit ? 'Edit talent template' : 'Create talent template'}>
      <ModalFrame size="lg" className="-m-3 w-[calc(100%+1.5rem)] rounded-none sm:m-0 sm:w-full sm:rounded-lg">
        <form onSubmit={handleSubmit} className="contents">
        {/* ── Header ─────────────────────────────────────────────────── */}
        <ModalFrame.Header className="items-center px-4 py-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-sm shrink-0">
              <LayoutTemplate size={15} className="text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-light-text-primary dark:text-dark-text-primary leading-tight">
                {isEdit ? 'Edit Template' : 'New Talent Template'}
              </h2>
              <p className="truncate text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                Configure posting criteria & content blocks
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={saving} aria-label="Close" className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-light-text-tertiary hover:bg-light-bg-secondary dark:text-dark-text-tertiary dark:hover:bg-dark-bg-tertiary disabled:opacity-50">
            <X className="h-5 w-5" />
          </button>
        </ModalFrame.Header>

        {/* ── Body ───────────────────────────────────────────────────── */}
        <ModalFrame.Body className={`p-0 ${optionsLoading ? 'opacity-60 pointer-events-none' : ''}`}>
          <div className="space-y-6 p-4 sm:p-6">

            {/* Name + Status */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_160px]">
              <TextField
                label="Name"
                required
                size="small"
                fullWidth
                value={form.name}
                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. LinkedIn EN"
              />
              <div>
                <label className={LABEL}>Status <span className="text-red-400 normal-case font-normal">*</span></label>
                <Autocomplete
                  clearable
                  fullWidth
                  options={statusOptions}
                  value={form.status_id}
                  onChange={val => setForm(prev => ({ ...prev, status_id: val || null }))}
                  placeholder="Status..."
                  disabled={optionsLoading}
                />
              </div>
            </div>

            {/* ── Posting criteria ─────────────────────────────────── */}
            <div>
              <SectionDivider icon={Zap} label="Posting criteria" accent="text-amber-500 dark:text-amber-400" />
              <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-2 mb-3">
                Template is auto-matched to posts sharing all selected criteria. Leave blank to match any.
              </p>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className={LABEL}>Platform</label>
                  <Autocomplete
                    clearable
                    fullWidth
                    options={toolOptions}
                    value={form.tool_id}
                    onChange={val => {
                      const tool = val ? toolOptions.find(t => Number(t.value) === Number(val)) : null;
                      const ic   = form.inner_client_id ? innerClientRaw.find(c => Number(c.id) === Number(form.inner_client_id)) : null;
                      setForm(prev => ({ ...prev, tool_id: val || null, prompt_id: null, name: buildName(tool?.label, ic?.name) }));
                    }}
                    placeholder="Any..."
                    disabled={optionsLoading}
                  />
                </div>
                <div>
                  <label className={LABEL}>Inner Client</label>
                  <Autocomplete
                    clearable
                    fullWidth
                    options={innerClientOptions}
                    value={form.inner_client_id}
                    onChange={val => {
                      const ic   = val ? innerClientRaw.find(c => Number(c.id) === Number(val)) : null;
                      const tool = form.tool_id ? toolOptions.find(t => Number(t.value) === Number(form.tool_id)) : null;
                      setForm(prev => ({ ...prev, inner_client_id: val || null, inner_client_iso: ic?.iso || '', prompt_id: null, name: buildName(tool?.label, ic?.name) }));
                    }}
                    placeholder="Any..."
                    disabled={optionsLoading}
                  />
                </div>
              </div>
            </div>

            {/* ── Prompt ───────────────────────────────────────────── */}
            <div>
              <SectionDivider icon={Sparkles} label="AI Prompt" accent="text-violet-500 dark:text-violet-400" />
              <div className="mt-3 space-y-2">
                <label className={LABEL}>Prompt for post generation <span className="text-red-400 normal-case font-normal">*</span></label>
                <Autocomplete
                  clearable
                  fullWidth
                  options={promptOptions}
                  value={form.prompt_id}
                  onChange={val => setForm(prev => ({ ...prev, prompt_id: val || null }))}
                  placeholder="Select prompt..."
                  disabled={optionsLoading}
                />

                {/* Suggestions */}
                {(promptMatch.exact || promptMatch.suggested.length > 0) && (
                  <div className="rounded-xl border border-light-border-primary dark:border-dark-border-primary overflow-hidden">
                    {promptMatch.exact && (
                      <button
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, prompt_id: String(promptMatch.exact.id) }))}
                        className={`w-full flex items-center gap-2 px-3 py-2.5 text-xs transition-colors border-b border-light-border-primary dark:border-dark-border-primary last:border-b-0 ${
                          form.prompt_id === String(promptMatch.exact.id)
                            ? 'bg-emerald-50 dark:bg-emerald-900/25'
                            : 'bg-light-bg-primary dark:bg-dark-bg-primary hover:bg-emerald-50/60 dark:hover:bg-emerald-900/10'
                        }`}
                      >
                        <Sparkles size={11} className="text-emerald-500 shrink-0" />
                        <span className="flex-1 min-w-0 truncate text-left text-light-text-primary dark:text-dark-text-primary">
                          {promptMatch.exact.name}
                        </span>
                        <span className="shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                          exact match
                        </span>
                        {form.prompt_id === String(promptMatch.exact.id) && <Check size={11} className="text-emerald-500 shrink-0" />}
                      </button>
                    )}
                    {promptMatch.suggested.slice(0, 3).map(p => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, prompt_id: String(p.id) }))}
                        className={`w-full flex items-center gap-2 px-3 py-2.5 text-xs transition-colors border-b border-light-border-primary dark:border-dark-border-primary last:border-b-0 ${
                          form.prompt_id === String(p.id)
                            ? 'bg-violet-50 dark:bg-violet-900/20'
                            : 'bg-light-bg-primary dark:bg-dark-bg-primary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-tertiary'
                        }`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-light-border-primary dark:bg-dark-border-secondary" />
                        <span className="flex-1 min-w-0 truncate text-left text-light-text-secondary dark:text-dark-text-secondary">
                          {p.name}
                        </span>
                        {p._total > 0 && (
                          <span className="shrink-0 px-1.5 py-0.5 rounded-full text-[10px] bg-light-bg-secondary dark:bg-dark-bg-tertiary text-light-text-tertiary dark:text-dark-text-tertiary">
                            {p._matched}/{p._total}
                          </span>
                        )}
                        {form.prompt_id === String(p.id) && <Check size={11} className="text-violet-500 shrink-0" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ── Content Blocks ───────────────────────────────────── */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <SectionDivider label="Content Blocks" />
              </div>
              <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-3">
                Select which talent data sections to include when generating posts.{' '}
                <span className="font-medium text-light-text-secondary dark:text-dark-text-secondary">
                  {activeBlocks} of {Object.keys(BLOCK_META).length} active
                </span>
              </p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(BLOCK_META).map(([key, { label, icon }]) => (
                  <BlockChip
                    key={key}
                    label={label}
                    icon={icon}
                    active={!!form.blocks_config[key]}
                    onClick={() => handleBlockToggle(key)}
                  />
                ))}
              </div>
            </div>

            {/* ── Draft toggle ─────────────────────────────────────── */}
            <div className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/10">
              <div>
                <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">Draft mode</p>
                <p className="text-xs text-amber-600/70 dark:text-amber-500/70">Hidden from post creation selectors</p>
              </div>
              <Toggle
                checked={form.is_draft}
                onChange={(checked) => setForm(prev => ({ ...prev, is_draft: checked }))}
                color="warning"
              />
            </div>

          </div>
        </ModalFrame.Body>

          {/* ── Footer ───────────────────────────────────────────────── */}
          <ModalFrame.Footer className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end [&>*]:w-full sm:[&>*]:w-auto">
            <Button type="button" variant="text" color="neutral" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" color="primary" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Template'}
            </Button>
          </ModalFrame.Footer>
        </form>
      </ModalFrame>
    </ModalPortal>
  );
};

export default TalentTemplateModal;
